import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  FooterConfig,
  FooterElement,
  LayoutConfigService,
} from '../../services/navigation/footer-sidebar.service';
import { MenuItem } from 'primeng/api';

export interface NavigationItem {
  name: string;
  href: string;
  icon: string;
  current?: boolean;
  children?: NavigationItem[];
  id?: string;
  disabled?: boolean;
}

export interface LogoConfig {
  type: 'image' | 'svg';
  src?: string;
  svgContent?: string;
  alt: string;
  class?: string;
}

export interface AvatarConfig {
  type: 'icon' | 'initials' | 'image';
  value?: string;
  backgroundColor?: string;
  color?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private layoutConfigService = inject(LayoutConfigService);

  private _logo = new BehaviorSubject<LogoConfig>({
    type: 'image',
    src: '/assets/images/logo.png',
    alt: 'Logo',
  });

  get logo() {
    return this._logo.value;
  }

  get logoChanges() {
    return this._logo.asObservable();
  }

  private _navigation = new BehaviorSubject<NavigationItem[]>([
    {
      name: 'Conexiones',
      href: '/conexiones',
      icon: 'pi pi-link',
      current: false,
    },
    {
      name: 'Monitoreo',
      href: '/monitoreo',
      icon: 'pi pi-chart-line',
      current: false,
    },
  ]);

  get navigation() {
    return this._navigation.value;
  }

  private _userNavigation = new BehaviorSubject<MenuItem[]>([]);
  public userNavigationChanges = this._userNavigation.asObservable();

  get userNavigation(): MenuItem[] {
    return this._userNavigation.value;
  }

  get navigationChanges() {
    return this._navigation.asObservable();
  }

  private _accentColor = new BehaviorSubject<string | undefined>(undefined);

  get accentColor(): string | undefined {
    return this._accentColor.value;
  }

  get accentColorChanges() {
    return this._accentColor.asObservable();
  }

  updateAccentColor(color: string | undefined) {
    this._accentColor.next(color);
  }

  private _topBarBgColor = new BehaviorSubject<string | undefined>(undefined);

  get topBarBgColor(): string | undefined {
    return this._topBarBgColor.value;
  }

  get topBarBgColorChanges() {
    return this._topBarBgColor.asObservable();
  }

  updateTopBarBgColor(color: string | undefined) {
    this._topBarBgColor.next(color);
  }

  private _avatarConfig = new BehaviorSubject<AvatarConfig>({ type: 'icon', value: 'pi pi-user' });

  get avatarConfig(): AvatarConfig {
    return this._avatarConfig.value;
  }

  get avatarConfigChanges() {
    return this._avatarConfig.asObservable();
  }

  updateAvatarConfig(config: AvatarConfig) {
    this._avatarConfig.next(config);
  }

  private _background = new BehaviorSubject<string | undefined>(undefined);

  get background(): string | undefined {
    return this._background.value;
  }

  get backgroundChanges() {
    return this._background.asObservable();
  }

  updateUserNavigation(items: MenuItem[]) {
    this._userNavigation.next(items);
  }

  updateLogo(logo: LogoConfig) {
    this._logo.next(logo);
  }

  updateNavigation(items: NavigationItem[]) {
    this._navigation.next(items);
  }

  setCurrentNavigationItem(href: string) {
    const updatedNavigation = this.navigation.map((item) => {
      const updatedItem = {
        ...item,
        current: item.href === href,
      };

      // Ensure children are preserved
      if (item.children && item.children.length > 0) {
        updatedItem.children = [...item.children];
      }

      return updatedItem;
    });

    this._navigation.next(updatedNavigation);
  }

  updateBackground(url: string | undefined) {
    this._background.next(url);
  }

  updateFooterConfig(config: FooterConfig): void {
    this.layoutConfigService.setFooterConfig(config);
  }

  showDefaultFooter(): void {
    this.layoutConfigService.showDefaultFooter();
  }

  hideDefaultFooter(): void {
    this.layoutConfigService.hideDefaultFooter();
  }

  setCustomFooter(elements: FooterElement[]): void {
    this.layoutConfigService.setCustomFooter(elements);
  }
}
